import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { StatusBadge } from "../../../constants/statusConstants";
import { AuthContext } from "../../../contexts/AuthContext";
import { departmentService } from "../../../services/api/departmentService";
import { formatDate } from "../../../utils/DateFormat";
import CustomTable from "../components/CustomTable";
import Loading from "../components/Loading";
import SearchAndFilterBar from "../components/SearchAndFilterBar";
import CreateDepartmentPopup from "./components/CreateDepartmentPopup";
import StaffPreview from "./components/popup/StaffPreview";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";

const columns = [
  {
    key: "stt",
    title: "STT",
    minWidth: "60px",
    render: (v) => <div className="pl-3">{v}</div>,
  },
  { key: "name", title: "Tên phòng ban", minWidth: "200px" },
  { key: "description", title: "Mô tả", minWidth: "260px" },
  { key: "createdAt", title: "Ngày tạo", minWidth: "150px" },
  {
    key: "staffs",
    title: "Nhân sự",
    minWidth: "260px",
    render: (staffs = []) => {
      if (!staffs?.length) {
        return <span className="text-gray-400 text-xs">Chưa có nhân sự</span>;
      }

      const MAX_SHOW = 3;
      const visibleStaffs = staffs?.slice(0, MAX_SHOW);
      const remaining = staffs?.length - MAX_SHOW;

      return (
        <StaffPreview
          staffs={staffs}
          visibleStaffs={visibleStaffs}
          remaining={remaining}
        />
      );
    },
  },

  {
    key: "status",
    title: "Trạng thái",
    minWidth: "160px",
    render: (value) => <StatusBadge status={value} className="min-w-[120px]" />,
  },
];

const DepartmentManager = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const [tableLoading, setTableLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [data, setData] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [canDelete, setCanDelete] = useState(false);

  const [draftSearch, setDraftSearch] = useState(
    searchParams.get("search") || "",
  );
  const [draftFromDate, setDraftFromDate] = useState(
    searchParams.get("fromDate") || "",
  );
  const [draftToDate, setDraftToDate] = useState(
    searchParams.get("toDate") || "",
  );

  const [search, setSearch] = useState(draftSearch);
  const [fromDate, setFromDate] = useState(draftFromDate);
  const [toDate, setToDate] = useState(draftToDate);

  const currentPage = Number(searchParams.get("page")) || 1;

  const fetchData = useCallback(async () => {
    try {
      setTableLoading(true);

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        ...(search ? { search_value: search } : {}),
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
      };

      const res = await departmentService.get_list_department(token, params);

      const { departments, pagination } = res.data;

      const mapped = (departments || []).map((item, index) => ({
        stt: (currentPage - 1) * itemsPerPage + index + 1,
        ...item,
        nav_id: item._id,
        createdAt: formatDate(item.createdAt),
      }));

      setData(mapped);
      setTotalPages(pagination?.totalPages || 1);
    } catch (error) {
      toast.dismiss();
      toast.error("Không thể tải danh sách phòng ban");
      console.error(error);
    } finally {
      setTableLoading(false);
    }
  }, [token, currentPage, itemsPerPage, search, fromDate, toDate]);

  useEffect(() => {
    fetchData();
    if (user?.role === "SuperAdmin") setCanDelete(true);
  }, [fetchData, user?.role]);

  const fetchDepartmentDetail = async (id) => {
    try {
      setDetailLoading(true);
      const res = await departmentService.get_detail_department(token, id);
      return res.data;
    } catch (error) {
      toast.error("Không thể tải chi tiết phòng ban");
      console.error(error);
      return null;
    } finally {
      setDetailLoading(false);
    }
  };

  const handleEdit = async (id) => {
    const detailData = await fetchDepartmentDetail(id);
    if (!detailData) return;

    setEditingDepartment(detailData);
    setIsEditOpen(true);
  };

  const handleView = async (id) => {
    const detailData = await fetchDepartmentDetail(id);
    if (!detailData) return;

    setEditingDepartment(detailData);
    setIsViewOpen(true);
  };

  const handleDelete = (id) => {
    setDeleteTarget(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setTableLoading(true);
      await departmentService.delete_department(token, deleteTarget);
      toast.success("Xóa phòng ban thành công!");
      setShowDeleteModal(false);
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Không thể xóa phòng ban.");
    } finally {
      setTableLoading(false);
    }
  };

  const handleSearchApply = () => {
    setSearch(draftSearch);
    setFromDate(draftFromDate);
    setToDate(draftToDate);

    const p = new URLSearchParams();
    if (draftSearch) p.set("search", draftSearch);
    if (draftFromDate) p.set("fromDate", draftFromDate);
    if (draftToDate) p.set("toDate", draftToDate);
    p.set("page", "1");

    setSearchParams(p);
  };

  const handleCreateDepartment = async (payload) => {
    try {
      await departmentService.create_department(token, payload);
      setIsCreateOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Tạo phòng ban thất bại");
      console.error(error);
      throw error;
    }
  };

  const handleUpdateDepartment = async (payload) => {
    try {
      await departmentService.update_department(
        token,
        editingDepartment._id,
        payload,
      );
      fetchData();
    } catch (error) {
      toast.error("Cập nhật phòng ban thất bại");
      console.error(error);
      throw error;
    }
  };

  return (
    <div className="flex flex-col h-full gap-8">
      <div className="shrink-0">
        <SearchAndFilterBar
        searchPlaceholder="Tìm kiếm phòng ban"
        searchValue={draftSearch}
        onSearchChange={(e) => setDraftSearch(e.target.value)}
        onSearchClick={handleSearchApply}
        showDatePickers
        showFilter
        filterLabel="Áp dụng"
        fromDatePlaceholder="Từ ngày"
        fromDateValue={draftFromDate}
        toDatePlaceholder="Đến ngày"
        toDateValue={draftToDate}
        onFromDateChange={setDraftFromDate}
        onToDateChange={setDraftToDate}
        onAddClick={() => setIsCreateOpen(true)}
        addButtonLabel="Thêm mới phòng ban"
        addButtonIcon="/icons/fi-plus-circle.svg"
        />
      </div>

      <div className="flex-1 min-h-0">
        <CustomTable
        data={data}
        columns={columns}
        currentPage={currentPage}
        setCurrentPage={(page) =>
          setSearchParams((prev) => {
            const p = new URLSearchParams(prev);
            p.set("page", page.toString());
            return p;
          })
        }
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        basePath="/quan-ly-phong-ban"
        handleEdit={handleEdit}
        handleView={handleView}
        handleDelete={handleDelete}
        setView
        setEdit
        setDelete={canDelete}
        loading={tableLoading}
        loadingText="Đang tải danh sách phòng ban..."
        />
      </div>

      {detailLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6">
            <Loading />
            <p className="mt-4 text-gray-700">Đang tải chi tiết phòng ban...</p>
          </div>
        </div>
      )}

      {isCreateOpen && (
        <CreateDepartmentPopup
          closePopup={() => setIsCreateOpen(false)}
          onSubmit={handleCreateDepartment}
        />
      )}

      {isEditOpen && editingDepartment && !detailLoading && (
        <CreateDepartmentPopup
          canEdit
          initialData={editingDepartment}
          closePopup={() => {
            setIsEditOpen(false);
            setEditingDepartment(null);
          }}
          onSubmit={handleUpdateDepartment}
        />
      )}

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteTarget(null);
        }}
        onConfirm={confirmDelete}
        title="Xác nhận xóa phòng ban"
        message="Bạn có chắc chắn muốn xóa phòng ban này không?"
        loading={tableLoading}
      />
    </div>
  );
};

export default DepartmentManager;
